import { useEffect } from 'react';
import { goToSelfSchedule } from '../utils/selfSchedule';
import { CONSULT_FORM_ANCHORS } from '../utils/selfScheduleConfig';

// Self-schedule experiment CTA interceptor (WEBDEV-3681; first used on /specials).
//
// Self-schedule pages are often composed in Builder.io, so the on-page-form CTAs
// (hero "Book My Free Consult", ShavingWaxingCalc "Book A Consult", the header
// "FREE CONSULT", etc.) are placed by the CMS rather than authored in our page
// templates — editing each component can't reliably reach them.
//
// They all share one thing: they point at an on-page consult-form anchor (e.g.
// #consult, #googlelp-quote — see CONSULT_FORM_ANCHORS). So when the variant is
// active we intercept clicks on those links at the document level (capture
// phase, before Gatsby's <Link> navigation) and redirect to /schedule instead.
// This covers Builder-placed and React CTAs uniformly without touching
// individual components.
//
// These are action buttons (not links to shareable content), so we also
// intercept modified clicks (Cmd/Ctrl/Shift) and middle-clicks — for those a
// bare hash href would otherwise just open a useless same-page anchor in a new
// tab. We honor new-tab intent by sending /schedule to a new tab instead.
//
// Limitation: only catches anchors targeting one of CONSULT_FORM_ANCHORS on the
// current page. A CTA that scrolls to the form via JS (no anchor href) would not
// be caught — none of the current self-schedule pages' CTAs do that.
export function useSelfScheduleCtaRedirect(active) {
    useEffect(() => {
        if (!active || typeof document === 'undefined') return;

        const handler = (event) => {
            // One guard serves both listeners: `click` only fires with button 0
            // (left / keyboard), `auxclick` only with 1 (middle) or 2 (right). So
            // this allows left + middle through and blocks right (its context menu
            // is left intact). Also skip events something else already handled.
            if (event.defaultPrevented || (event.button !== 0 && event.button !== 1)) return;

            const anchor = event.target?.closest?.('a[href]');
            if (!anchor) return;

            // Only redirect CTAs that scroll to THIS page's consult form, not
            // links that navigate elsewhere (a cross-page href can also end in
            // #consult). anchor.href resolves to an absolute URL, so this handles
            // "#consult", "/specials#consult", and trailing-slash variants alike.
            let url;
            try {
                url = new URL(anchor.href, window.location.href);
            } catch {
                return;
            }
            if (url.pathname !== window.location.pathname || !CONSULT_FORM_ANCHORS.includes(url.hash)) {
                return;
            }

            // preventDefault alone stops the navigation: reach-router's <Link>
            // skips navigate() when defaultPrevented is set, and it cancels the
            // browser's default anchor/middle-click behavior. We deliberately do
            // NOT stopPropagation — the event must still bubble so GTM/analytics
            // click tracking on these CTAs fires (this is a conversion experiment;
            // killing propagation would blind our own telemetry on the variant arm).
            event.preventDefault();

            // Open /schedule in a new tab when the user signaled new-tab intent
            // (modifier key, or a middle-click that arrives via auxclick).
            const newTab = event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1;
            goToSelfSchedule(newTab);
        };

        // Capture phase so we set defaultPrevented before Gatsby <Link>'s
        // bubble-phase handler reads it (that's what cancels the SPA nav).
        // auxclick covers middle-clicks, which modern browsers don't fire as click.
        document.addEventListener('click', handler, true);
        document.addEventListener('auxclick', handler, true);
        return () => {
            document.removeEventListener('click', handler, true);
            document.removeEventListener('auxclick', handler, true);
        };
    }, [active]);
}
